import { translate } from '@thoughtspot/i18n';
import { OptionsGroup } from '@thoughtspot/radiant-react/widgets/select/base-select';
import _ from 'lodash';
import {
    DateFilterType,
    DatePeriod,
    FilterColumn,
    FilterTypes,
} from '/@services/generated/graphql-types';
import { FilterErrorConfig } from '../../../answer-filter.util';
import { DateFilterProto, DateValues } from '../../date-filter';
import {
    RollingDateFilterOperatorLabels,
    RollingDateFilterOperators,
} from './rolling-date-filter.constants';
import {
    filterDropdownOptions,
    futureDateOperators,
} from './rolling-date-filter-operands.util';

// Re-export enums for backward compatibility
export { RollingDateFilterOperatorLabels, RollingDateFilterOperators };

/**
 * Props for the RollingDateFilterContainer component
 * @typedef {Object} RollingDateFilterProps
 *
 * @property {DateFilterProto} selectedFilter - The currently selected date filter
 * @property {function(DateFilterProto): void} onFilterChange - Callback function to handle when the date filter is changed
 * @property {boolean} isReadOnly - Can the filter be edited
 * @property {FilterColumn} [filterColumn] - Column on which the filter is being applied
 * @property {React.Dispatch<React.SetStateAction<FilterErrorConfig>>} [setAlertConfig] -
 *   Used to set error/warning alerts shown in parent components
 */
export interface RollingDateFilterContainerProps {
    selectedFilter: DateFilterProto;
    onFilterChange: (dateFilter: DateFilterProto) => void;
    isReadOnly: boolean;
    filterColumn: FilterColumn;
    setAlertConfig: React.Dispatch<React.SetStateAction<FilterErrorConfig>>;
}

export interface RollingDateFilterProps
    extends Omit<
        RollingDateFilterContainerProps,
        'filterColumn' | 'setAlertConfig'
    > {
    hideTimeBasedFilters?: boolean;
    hideFutureFilters?: boolean;
    showIncludeCurrentPeriod?: boolean;
    isIncludeCurrentPeriodDisabled?: boolean;
    includeCurrentPeriodDisabledTooltip?: string;
    customOperandsInputBoxStyle?: boolean;
}

/**
Object containing mapping of RollingDateFilterOperators to DateFilterProto objects.
*/
export const operatorToDateTypeAndFilterType: {
    [key in RollingDateFilterOperators]: DateFilterProto;
} = {
    [RollingDateFilterOperators.YESTERDAY]: {
        filterType: FilterTypes.Eq,
        dateFilterType: DateFilterType.Yesterday,
        dateValue: null,
        isNegate: false,
    },
    [RollingDateFilterOperators.TODAY]: {
        filterType: FilterTypes.Eq,
        dateFilterType: DateFilterType.Today,
        dateValue: null,
        isNegate: false,
    },
    [RollingDateFilterOperators.TOMORROW]: {
        filterType: FilterTypes.Eq,
        dateFilterType: DateFilterType.Tomorrow,
        dateValue: null,
        isNegate: false,
    },
    [RollingDateFilterOperators.THIS]: {
        filterType: FilterTypes.Eq,
        dateFilterType: DateFilterType.ThisPeriod,
        dateValue: {
            datePeriod: DatePeriod.Month,
            number: null,
        },
        isNegate: false,
    },
    [RollingDateFilterOperators.LAST]: {
        filterType: FilterTypes.Eq,
        dateFilterType: DateFilterType.LastNPeriod,
        dateValue: {
            datePeriod: DatePeriod.Month,
            number: 1,
        },
        isNegate: false,
    },
    [RollingDateFilterOperators.NEXT]: {
        filterType: FilterTypes.Eq,
        dateFilterType: DateFilterType.NextNPeriod,
        dateValue: {
            datePeriod: DatePeriod.Month,
            number: 1,
        },
        isNegate: false,
    },
    [RollingDateFilterOperators.AFTER]: {
        filterType: FilterTypes.Gt,
        dateFilterType: DateFilterType.ThisPeriod,
        dateValue: {
            datePeriod: DatePeriod.Month,
            number: null,
        },
        isNegate: false,
    },
    [RollingDateFilterOperators.ON_OR_AFTER]: {
        filterType: FilterTypes.Ge,
        dateFilterType: DateFilterType.ThisPeriod,
        dateValue: {
            datePeriod: DatePeriod.Month,
            number: null,
        },
        isNegate: false,
    },
    [RollingDateFilterOperators.BEFORE]: {
        filterType: FilterTypes.Lt,
        dateFilterType: DateFilterType.ThisPeriod,
        dateValue: {
            datePeriod: DatePeriod.Month,
            number: null,
        },
        isNegate: false,
    },
    [RollingDateFilterOperators.ON_OR_BEFORE]: {
        filterType: FilterTypes.Le,
        dateFilterType: DateFilterType.ThisPeriod,
        dateValue: {
            datePeriod: DatePeriod.Month,
            number: null,
        },
        isNegate: false,
    },
    [RollingDateFilterOperators.WEEK_TO_DATE]: {
        filterType: FilterTypes.Eq,
        dateFilterType: DateFilterType.PeriodToDate,
        dateValue: {
            datePeriod: DatePeriod.Week,
        },
        isNegate: false,
    },
    [RollingDateFilterOperators.MONTH_TO_DATE]: {
        filterType: FilterTypes.Eq,
        dateFilterType: DateFilterType.PeriodToDate,
        dateValue: {
            datePeriod: DatePeriod.Month,
        },
        isNegate: false,
    },
    [RollingDateFilterOperators.QUARTER_TO_DATE]: {
        filterType: FilterTypes.Eq,
        dateFilterType: DateFilterType.PeriodToDate,
        dateValue: {
            datePeriod: DatePeriod.Quarter,
        },
        isNegate: false,
    },
    [RollingDateFilterOperators.YEAR_TO_DATE]: {
        filterType: FilterTypes.Eq,
        dateFilterType: DateFilterType.PeriodToDate,
        dateValue: {
            datePeriod: DatePeriod.Year,
        },
        isNegate: false,
    },
};

/**
 * Generates an array of dropdown options groups for rolling date filter operators.
 * Each option group contains options for a specific date range.
 *
 * @param {boolean} hideFutureFilters - Date operators that refer to
 *   future options like 'next' and 'tomorrow` will be filtered out when `true`.
 *
 * @returns {OptionsGroup[]} An array of option groups for rolling date filter operators.
 */
export const getRollingDateFilterOperatorOptions = (
    hideFutureFilters: boolean,
): OptionsGroup[] => {
    let optionsGroups = [
        {
            title: '',
            options: [
                {
                    id: RollingDateFilterOperators.YESTERDAY,
                    label: translate(RollingDateFilterOperatorLabels.YESTERDAY),
                },
                {
                    id: RollingDateFilterOperators.TODAY,
                    label: translate(RollingDateFilterOperatorLabels.TODAY),
                },
                {
                    id: RollingDateFilterOperators.TOMORROW,
                    label: translate(RollingDateFilterOperatorLabels.TOMORROW),
                },
            ],
        },
        {
            title: '',
            options: [
                {
                    id: RollingDateFilterOperators.THIS,
                    label: translate(RollingDateFilterOperatorLabels.THIS),
                },
                {
                    id: RollingDateFilterOperators.LAST,
                    label: translate(RollingDateFilterOperatorLabels.LAST),
                },
                {
                    id: RollingDateFilterOperators.NEXT,
                    label: translate(RollingDateFilterOperatorLabels.NEXT),
                },
            ],
        },
        {
            title: '',
            options: [
                {
                    id: RollingDateFilterOperators.BEFORE,
                    label: translate(RollingDateFilterOperatorLabels.BEFORE),
                },
                {
                    id: RollingDateFilterOperators.AFTER,
                    label: translate(RollingDateFilterOperatorLabels.AFTER),
                },
                {
                    id: RollingDateFilterOperators.ON_OR_AFTER,
                    label: translate(
                        RollingDateFilterOperatorLabels.ON_OR_AFTER,
                    ),
                },
                {
                    id: RollingDateFilterOperators.ON_OR_BEFORE,
                    label: translate(
                        RollingDateFilterOperatorLabels.ON_OR_BEFORE,
                    ),
                },
            ],
        },
        {
            title: '',
            options: [
                {
                    id: RollingDateFilterOperators.WEEK_TO_DATE,
                    label: translate(
                        RollingDateFilterOperatorLabels.WEEK_TO_DATE,
                    ),
                },
                {
                    id: RollingDateFilterOperators.MONTH_TO_DATE,
                    label: translate(
                        RollingDateFilterOperatorLabels.MONTH_TO_DATE,
                    ),
                },
                {
                    id: RollingDateFilterOperators.QUARTER_TO_DATE,
                    label: translate(
                        RollingDateFilterOperatorLabels.QUARTER_TO_DATE,
                    ),
                },
                {
                    id: RollingDateFilterOperators.YEAR_TO_DATE,
                    label: translate(
                        RollingDateFilterOperatorLabels.YEAR_TO_DATE,
                    ),
                },
            ],
        },
    ];
    if (hideFutureFilters) {
        optionsGroups = optionsGroups.map(option => ({
            ...option,
            options: filterDropdownOptions(option.options, futureDateOperators),
        }));
    }
    return optionsGroups;
};

/**
The mapOperatorByFilterType constant is an object that maps FilterTypes value to a corresponding RollingDateFilterOperators value.
*/
export const mapOperatorByFilterType: {
    [key in FilterTypes]?: RollingDateFilterOperators;
} = {
    [FilterTypes.Lt]: RollingDateFilterOperators.BEFORE,
    [FilterTypes.Le]: RollingDateFilterOperators.ON_OR_BEFORE,
    [FilterTypes.Gt]: RollingDateFilterOperators.AFTER,
    [FilterTypes.Ge]: RollingDateFilterOperators.ON_OR_AFTER,
};

/**
The mapOperatorByDateFilterType constant is an object that maps DateFilterType value to a corresponding RollingDateFilterOperators value.
*/
export const mapOperatorByDateFilterType: {
    [key in DateFilterType]?: RollingDateFilterOperators;
} = {
    [DateFilterType.Yesterday]: RollingDateFilterOperators.YESTERDAY,
    [DateFilterType.Today]: RollingDateFilterOperators.TODAY,
    [DateFilterType.Tomorrow]: RollingDateFilterOperators.TOMORROW,
    [DateFilterType.ThisPeriod]: RollingDateFilterOperators.THIS,
    [DateFilterType.LastNPeriod]: RollingDateFilterOperators.LAST,
    [DateFilterType.NextNPeriod]: RollingDateFilterOperators.NEXT,
};

/**
The mapOperatorByPeriod constant is an object that maps DatePeriod value to a corresponding RollingDateFilterOperators value.
*/
export const mapOperatorByPeriod: {
    [key in DatePeriod]?: RollingDateFilterOperators;
} = {
    [DatePeriod.Week]: RollingDateFilterOperators.WEEK_TO_DATE,
    [DatePeriod.Month]: RollingDateFilterOperators.MONTH_TO_DATE,
    [DatePeriod.Quarter]: RollingDateFilterOperators.QUARTER_TO_DATE,
    [DatePeriod.Year]: RollingDateFilterOperators.YEAR_TO_DATE,
};

/**
Maps the given filterType, dateFilterType and dateValue to a RollingDateFilterOperator.
@param {FilterTypes} filterType - The filter type.
@param {DateFilterType} dateFilterType - The date filter type.
@param {DateFilterValue} dateValue - The date value.
@returns {RollingDateFilterOperators | null} - The RollingDateFilterOperator corresponding to the input values.
*/
export const getRollingDateFilterOperator = (
    filterType: FilterTypes,
    dateFilterType: DateFilterType,
    dateValue: DateValues,
): RollingDateFilterOperators | null => {
    if (filterType && filterType !== FilterTypes.Eq) {
        return mapOperatorByFilterType[filterType];
    }
    if (_.isEqual(dateFilterType, DateFilterType.PeriodToDate)) {
        return mapOperatorByPeriod[dateValue?.datePeriod];
    }
    if (dateFilterType) {
        return mapOperatorByDateFilterType[dateFilterType];
    }
    return RollingDateFilterOperators.YESTERDAY;
};
